<template>
  <div class="fts-history-log">
    <div class="hl-entry" v-for="(x, xInd) in getLog" :key="x.id || xInd">
      <!-- table field: a full-width block (name/counts line, then the changed rows) -->
      <div class="hl-table" v-if="x.isTable">
        <div class="hl-field">
          <span class="hl-field-name">{{ x.DisplayName }}</span>
          <span class="hl-counts" v-if="x.counts">
            <span class="hl-ct hl-ct--add" v-if="x.counts.added">+{{ x.counts.added }}</span>
            <span class="hl-ct hl-ct--rem" v-if="x.counts.removed">&minus;{{ x.counts.removed }}</span>
            <span class="hl-ct hl-ct--edt" v-if="x.counts.updated">~{{ x.counts.updated }}</span>
            <span class="hl-ct hl-ct--mov" v-if="x.counts.moved">&#8597;{{ x.counts.moved }}</span>
          </span>
        </div>

        <div class="hl-rows">
          <div
            class="hl-row"
            :class="`hl-row--${rowTypeClass(y.Type)}`"
            v-for="(y, yInd) in x.Details"
            :key="y.id || yInd"
          >
            <span class="hl-mark" aria-hidden="true">{{ rowMark(y.Type) }}</span>

            <!-- moved: the position change is the whole story -->
            <div class="hl-row-body hl-row-body--inline" v-if="y.Type === 3">
              <span class="hl-row-label hl-row-label--muted">Row</span>
              <span class="hl-change">
                <span class="hl-kv">moved</span>
                <span class="hl-pos">#{{ y.Original }}</span>
                <span class="hl-arrow">&rarr;</span>
                <span class="hl-pos">#{{ y.Row }}</span>
              </span>
            </div>

            <!-- rich rows (added / removed / multi-field): label, then an aligned field | value list -->
            <div class="hl-row-body hl-row-body--stacked" v-else-if="isStacked(y)">
              <span class="hl-row-label" v-if="rowLabel(y, yInd)">{{ rowLabel(y, yInd) }}</span>
              <div class="hl-cells" v-if="displayCells(y).length">
                <template v-for="(c, cInd) in displayCells(y)">
                  <span class="hl-cell-key" :class="{ 'hl-cell-key--full': isLongChange(c.Original, c.Current) }" :key="`k-${cInd}`">{{ c.DisplayName }}</span>
                  <span class="hl-cell-val" :class="{ 'hl-cell-val--stack': isLongChange(c.Original, c.Current) }" :key="`v-${cInd}`">
                    <fts-history-text-diff
                      v-if="isDiffable(c.Original, c.Current)"
                      :original="c.Original" :current="c.Current"
                    />
                    <template v-else>
                      <fts-history-log-display
                        v-if="!isEmpty(c.Original)"
                        :module-id="moduleId" :information-id="c.InformationId"
                        :data-type="dataType(c.Original)" :log="c.Original" class="hl-old"
                      />
                      <span class="hl-arrow" v-if="!isEmpty(c.Original) && !isEmpty(c.Current)">&rarr;</span>
                      <fts-history-log-display
                        v-if="!isEmpty(c.Current)"
                        :module-id="moduleId" :information-id="c.InformationId"
                        :data-type="dataType(c.Current)" :log="c.Current" class="hl-new"
                      />
                    </template>
                  </span>
                </template>
              </div>
              <fts-history-log
                v-for="(nc, ncInd) in nestedCells(y)"
                :key="`nt-${ncInd}`"
                :module-id="moduleId" :log="[nc]" class="hl-nested"
              />
            </div>

            <!-- simple rows (single-cell edit): stays on one line -->
            <div class="hl-row-body hl-row-body--inline" v-else>
              <span class="hl-row-label">{{ rowLabel(y, yInd) }}</span>
              <span class="hl-change" v-if="displayCells(y).length">
                <span class="hl-cell" v-for="(c, cInd) in displayCells(y)" :key="cInd">
                  <span class="hl-kv">{{ c.DisplayName }}</span>
                  <fts-history-log-display
                    v-if="!isEmpty(c.Original)"
                    :module-id="moduleId" :information-id="c.InformationId"
                    :data-type="dataType(c.Original)" :log="c.Original" class="hl-old"
                  />
                  <span class="hl-arrow" v-if="!isEmpty(c.Original) && !isEmpty(c.Current)">&rarr;</span>
                  <fts-history-log-display
                    v-if="!isEmpty(c.Current)"
                    :module-id="moduleId" :information-id="c.InformationId"
                    :data-type="dataType(c.Current)" :log="c.Current" class="hl-new"
                  />
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- scalar field: name (col 1) + value (col 2), aligned across every field -->
      <template v-else>
        <span
          class="hl-field-name hl-field-name--scalar"
          :class="{ 'hl-field-name--full': isLongChange(x.Original, x.Current) }"
        >{{ x.DisplayName }}<span class="hl-tag hl-tag--formula" v-if="x.Computed">Formula</span><span class="hl-tag hl-tag--auto" v-else-if="x.Atomic">Auto</span></span>
        <span class="hl-change" :class="{ 'hl-change--stack': isLongChange(x.Original, x.Current) }">
          <fts-history-text-diff
            v-if="isDiffable(x.Original, x.Current)"
            :original="x.Original" :current="x.Current"
          />
          <template v-else>
            <fts-history-log-display
              v-if="!isEmpty(x.Original)"
              :module-id="moduleId" :information-id="x.InformationId"
              :data-type="x.originalDataType" :log="x.Original" class="hl-old"
            />
            <span class="hl-arrow" v-if="!isEmpty(x.Original) && !isEmpty(x.Current)">&rarr;</span>
            <fts-history-log-display
              v-if="isEmpty(x.Original) || (!isEmpty(x.Original) && !isEmpty(x.Current))"
              :module-id="moduleId" :information-id="x.InformationId"
              :data-type="x.currentDataType" :log="x.Current" class="hl-new"
            />
          </template>
        </span>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import commons from '../../commons/commons';

import FTSHistoryLogDisplay from './FTSHistoryLogDisplay';
import FTSHistoryTextDiff from './FTSHistoryTextDiff';

// A date or full ISO timestamp: date-only, or with time, optional milliseconds and
// optional Z/offset. Broader than the shared dateTimeValidator (which rejects the
// millisecond form, so timestamps like ...33.608+08:00 used to leak as raw text).
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

// Classify a history value so FTSHistoryLogDisplay can render it (date, user,
// file, location, …). Shared by the scalar-field map and the per-cell renderer.
function computeDataType(val) {
  if (typeof val === 'boolean') {
    return 'yesno';
  }
  if (
    commons.isObject(val) && (
      (val.hasOwnProperty('lat') && val.hasOwnProperty('lng')) ||
      val.hasOwnProperty('placeName')
    )
  ) {
    return 'location';
  } else if (commons.isObject(val) && val.hasOwnProperty('path')) {
    return 'file';
  } else if (commons.isObject(val) && val.hasOwnProperty('username')) {
    return 'user';
  } else if (commons.isArray(val) && val.some(x => x.hasOwnProperty('path'))) {
    return 'files';
  } else if (commons.isArray(val) && val.length && val.every(x => commons.isObject(x) && x.hasOwnProperty('username'))) {
    return 'users';
  } else if (typeof val === 'string' && ISO_DATE_RE.test(val) && moment(val, moment.ISO_8601).isValid()) {
    return 'date';
  } else {
    return 'default';
  }
}

export default {
  name: "fts-history-log",
  components: {
    "fts-history-log-display": FTSHistoryLogDisplay,
    "fts-history-text-diff": FTSHistoryTextDiff,
  },
  props: {
    moduleId: [String, Number],
    log: Array,
  },
  methods: {
    isEmpty(log) {
      const isEmptyArray = (val) => val && val.length;
      const isEmptyObject = (val) => {
        for (var i in val) return false;
        return true;
      }
      return (
        log === null ||
        log === undefined ||
        log === '' ||
        (commons.isObject(log) && isEmptyArray(log)) ||
        (commons.isArray(log) && isEmptyObject(log))
      );
    },
    dataType(val) {
      return computeDataType(val);
    },
    // schemaVersion-2 change types: 0 Added, 1 Removed, 2 Updated, 3 Moved.
    rowTypeClass(type) {
      switch (type) {
        case 0: return 'added';
        case 1: return 'removed';
        case 3: return 'moved';
        default: return 'updated';
      }
    },
    rowMark(type) {
      switch (type) {
        case 0: return '+';
        case 1: return '−';  // minus
        case 3: return '↕';  // up-down arrow
        default: return '│'; // vertical bar (edited)
      }
    },
    tableCounts(details) {
      const c = { added: 0, removed: 0, updated: 0, moved: 0 };
      (details || []).forEach((r) => {
        if (r.Type === 0) c.added++;
        else if (r.Type === 1) c.removed++;
        else if (r.Type === 3) c.moved++;
        else c.updated++;
      });
      return c;
    },
    // Cells with a real InformationId — drops the internal {field}Id/{field}Username
    // reference companions that snapshots carry for recovery.
    rowCells(details) {
      if (!Array.isArray(details)) return [];
      return details.filter(cell => cell && cell.InformationId);
    },
    // Added/removed rows are identified by their cells (the content); edits keep the
    // positional index. A row with no cells at all (legacy v1) falls back to the index
    // so it isn't a bare mark.
    rowLabel(y, yInd) {
      const positional = `row ${y.Row ? y.Row : (yInd + 1)}`;
      if (y.Type === 0 || y.Type === 1) {
        return this.displayCells(y).length ? '' : positional;
      }
      return positional;
    },
    // Plain-value cells shown for a row: no companions, no nested tables, nothing empty.
    displayCells(y) {
      return this.rowCells(y.Details).filter(c =>
        !(Array.isArray(c.Details) && c.Details.length) &&
        !(this.isEmpty(c.Original) && this.isEmpty(c.Current))
      );
    },
    nestedCells(y) {
      return this.rowCells(y.Details).filter(c => Array.isArray(c.Details) && c.Details.length);
    },
    // A long text change reads better stacked (old over new, full width) than crammed
    // side-by-side into two narrow columns.
    isLongChange(original, current) {
      // only genuine long text stacks; dates/users/files (structured or formatted
      // short) never count, even if their raw string is lengthy
      const isLong = (v) => typeof v === 'string' && v.length > 40 && computeDataType(v) === 'default';
      return isLong(original) || isLong(current);
    },
    // A long edit with text on both sides gets the word-diff + Diff/Original/New toggle.
    isDiffable(original, current) {
      return this.isLongChange(original, current)
        && !this.isEmpty(original) && !this.isEmpty(current);
    },
    // Rich rows get the stacked, aligned field|value list; a lone short edit stays inline.
    isStacked(y) {
      const cells = this.displayCells(y);
      return cells.length >= 2
        || this.nestedCells(y).length > 0
        || cells.some(c => this.isLongChange(c.Original, c.Current));
    }
  },
  computed: {
    getLog() {
      return (this.log || [])
        .filter((x) => {
          const isTable = Array.isArray(x.Details) && x.Details.length > 0;
          // hide a field with no value and no change (e.g. empty fields captured on a
          // created record) — including its name; only show it when something changed
          return isTable || !(this.isEmpty(x.Original) && this.isEmpty(x.Current));
        })
        .map((x) => {
          const isTable = Array.isArray(x.Details) && x.Details.length > 0;
          return {
            ...x,
            isTable,
            counts: isTable ? this.tableCounts(x.Details) : null,
            currentDataType: computeDataType(x.Current),
            originalDataType: computeDataType(x.Original),
          };
        });
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-history-log {
  // one shared 2-column grid so every field name (col 1) and value (col 2)
  // line up across the whole log; table fields span both columns.
  display: grid;
  grid-template-columns: fit-content(45%) minmax(0, 1fr);
  column-gap: 0.75rem;
  row-gap: 0.25rem;
  align-items: baseline;
  font-size: 0.78rem;
  line-height: 1.5;

  // entries dissolve into the shared grid; their children become the grid items
  .hl-entry { display: contents; }

  // a table field is a full-width block that manages its own internal layout
  .hl-table {
    grid-column: 1 / -1;
    &:not(:first-child) { margin-top: 0.3rem; }
  }

  // scalar field: name in col 1, value in col 2 (long values span both)
  .hl-entry > .hl-field-name--scalar {
    grid-column: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .hl-entry > .hl-field-name--full { grid-column: 1 / -1; }
  .hl-entry > .hl-change { grid-column: 2; min-width: 0; }
  .hl-entry > .hl-change--stack { grid-column: 1 / -1; }

  // the table field header (name + counts) stays an inline flex row
  .hl-field {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    column-gap: 0.5rem;
    row-gap: 0.15rem;
  }
  // field names read as quiet labels — same grey as the cell labels inside a table
  .hl-field-name {
    font-weight: 500;
    color: var(--inistate-text-tertiary);
  }

  // table counts, muted, colour only per type
  .hl-counts {
    display: inline-flex;
    gap: 0.5rem;
    font-variant-numeric: tabular-nums;
  }
  // micro-label tier — same size as the provenance tags and the AI panel's badges,
  // so the whole history detail panel runs on just two sizes
  .hl-ct {
    font-weight: 600;
    font-size: 0.625rem;
    line-height: 1rem;
    white-space: nowrap;
    &.hl-ct--add { color: var(--fts-success); }
    &.hl-ct--rem { color: var(--fts-danger); }
    &.hl-ct--mov { color: var(--fts-primary); }
    &.hl-ct--edt { color: var(--inistate-text-tertiary); }
  }

  // rows
  .hl-rows {
    margin-top: 0.15rem;
  }
  .hl-row {
    display: grid;
    grid-template-columns: 0.9rem 1fr;
    column-gap: 0.5rem;
    align-items: baseline;
    padding: 0.14rem 0;
  }
  .hl-mark {
    text-align: center;
    font-weight: 700;
    line-height: 1.4;
    color: var(--inistate-text-tertiary);
  }
  .hl-row--added .hl-mark { color: var(--fts-success); }
  .hl-row--removed .hl-mark { color: var(--fts-danger); }
  .hl-row--moved .hl-mark { color: var(--fts-primary); }
  .hl-row--updated .hl-mark { font-weight: 500; }

  .hl-row-body {
    min-width: 0;
  }
  // simple rows: label + change on one wrapping line
  .hl-row-body--inline {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 0.45rem;
    row-gap: 0.1rem;
  }
  // rich rows: label above, then the aligned cell list
  .hl-row-body--stacked {
    display: block;
  }

  .hl-row-label {
    font-weight: 600;
    color: var(--inistate-text-primary);
    &.hl-row-label--muted {
      font-weight: 500;
      color: var(--inistate-text-secondary);
    }
  }

  // aligned field | value grid — labels form a column, values line up and wrap
  .hl-cells {
    display: grid;
    grid-template-columns: minmax(3.5rem, max-content) 1fr;
    column-gap: 0.75rem;
    row-gap: 0.15rem;
    margin-top: 0.1rem;
  }
  .hl-cell-key {
    grid-column: 1;
    white-space: nowrap;
    color: var(--inistate-text-tertiary);
  }
  // long cell: field name on its own line above the stacked old / → / new
  .hl-cell-key--full {
    grid-column: 1 / -1;
  }
  .hl-cell-val {
    grid-column: 2;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35rem;
    overflow-wrap: anywhere;
  }

  // long text: stack old over new (each full width) with the arrow between them,
  // instead of two cramped side-by-side columns
  .hl-change--stack,
  .hl-cell-val--stack {
    flex-direction: column;
    align-items: stretch;
    gap: 0.15rem;
  }
  // long cell value spans the full width, under its own-line field name
  .hl-cell-val--stack {
    grid-column: 1 / -1;
  }
  .hl-change--stack {
    flex-basis: 100%;
    width: 100%;
  }
  .hl-change--stack .hl-old,
  .hl-change--stack .hl-new,
  .hl-cell-val--stack .hl-old,
  .hl-cell-val--stack .hl-new {
    display: block;
  }

  // inline change expression
  .hl-change {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    color: var(--inistate-text-secondary);
    min-width: 0;
  }
  .hl-cell {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
  }
  // field labels never break mid-word
  .hl-kv,
  .hl-cell-key {
    white-space: nowrap;
    color: var(--inistate-text-tertiary);
  }
  .hl-arrow {
    color: var(--inistate-text-tertiary);
  }
  .hl-pos {
    color: var(--fts-primary);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  // values stay neutral: struck-grey old -> ink new. Colour lives only on the
  // marks (+ − ↕) and the counts, never on the values themselves.
  .hl-old {
    color: var(--inistate-text-tertiary);
    text-decoration: line-through;
    text-decoration-color: rgba(var(--fts-default-rgb), 0.5);
  }
  .hl-new {
    color: var(--inistate-text-primary);
    font-weight: 500;
  }

  // nested table, indented with a quiet guide (not a between-change divider)
  .hl-nested {
    display: block;
    margin-top: 0.25rem;
    padding-left: 0.6rem;
    border-left: 1px solid var(--inistate-border-primary);
  }

  // formula / atomic provenance tags (unchanged behaviour, quieter)
  .hl-tag {
    display: inline-flex;
    padding: 0 0.25rem;
    border-radius: 0.15rem;
    font-size: 0.625rem;
    line-height: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    &.hl-tag--formula {
      background-color: rgba(var(--fts-primary-rgb), 0.125);
      color: var(--fts-primary);
    }
    &.hl-tag--auto {
      background-color: rgba(var(--fts-warning-rgb), 0.125);
      color: var(--fts-warning);
    }
  }
}
</style>
